import { Button, DropdownMenu } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { Form } from 'components/organisms';
import {
  musicProductDigitalInitialValues,
  musicProductDigitalTabs,
} from 'components/organisms/forms/music-product-digital/MusicProductDigitalForm';
import { useComponentVisible, useHasPermission } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import {
  PiDotsThreeVerticalBold,
  PiPencilSimpleLine,
  PiStorefront,
} from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useDeleteMusicProductDigital,
  useExportMusicProductDigitalDDEX,
  useExportMusicProductDigitalTXT,
  useGetMusicProductDigital,
  usePutMusicProductDigitalFromTrack,
} from 'services/music-product-digital';
import { DIGITAL_PRODUCT_TYPE } from 'services/music-product-digital/types/enums';
import { usePostMusicProductPhysicalFromDigital } from 'services/music-product-physical';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getMainTitle } from 'utils/global/mainTitle.utils';
import { getLastTabPathname } from 'utils/navbar/getParentPathname';
import {
  onErrorShowAllErrorsToast,
  onErrorShowToast,
} from 'utils/service/errorHandler';

const MusicProductDigital = () => {
  const musicProductDigitalId = useParamId('music-product-digital');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const hasPermission = useHasPermission();

  const { t } = useTranslation();

  const { data: musicProductDigital, isLoading, refetch } =
    useGetMusicProductDigital(musicProductDigitalId);

  const removeMusicProductDigital = useDeleteMusicProductDigital(
    musicProductDigitalId
  );

  const updateFromTrack = usePutMusicProductDigitalFromTrack();

  const musicProductDigitalIndividualInitialValues = musicProductDigital
    ? middleware.musicProductDigital.queryToForm(musicProductDigital)
    : musicProductDigitalInitialValues;

  const goToEditMusicProductDigital = () => {
    musicProductDigital &&
      navigate(
        `/music-product-digital/edit/${musicProductDigitalId}/${getLastTabPathname(
          pathname
        )}`,
        {
          replace: true,
        }
      );
  };

  const onRemoveMusicProductDigital = async (actions: PopupActions) => {
    await removeMusicProductDigital.mutateAsync(undefined, {
      onError: error => {
        actions.close();
        onErrorShowToast(error);
      },
      onSuccess: () => navigate('/music-product-digital'),
    });
  };

  const dropdownVisible = useComponentVisible<HTMLDivElement>(false);

  const createPhysical = usePostMusicProductPhysicalFromDigital();
  const onCreateMusicProductPhysical = async () =>
    await createPhysical.mutateAsync(musicProductDigitalId, {
      onSuccess: physicalId =>
        navigate(`/music-product-physical/${physicalId}/tracks`),
      onError: error => onErrorShowAllErrorsToast(error),
    });

  const exportTXT = useExportMusicProductDigitalTXT();
  const exportDDEX = useExportMusicProductDigitalDDEX();

  const productName = getMainTitle(musicProductDigital?.mainTitle);

  const handleExportTXT = () => {
    dropdownVisible.handleComponentVisible();
    exportTXT.mutate({
      id: musicProductDigitalId,
      fileName: `${productName}.txt`,
    });
  };

  const handleExportDDEX43 = () => {
    dropdownVisible.handleComponentVisible();
    exportDDEX.mutate({
      id: musicProductDigitalId,
      ddexVersionId: 1,
      fileName: `${productName}_DDEX43.xml`,
    });
  };

  const handleExportDDEX382 = () => {
    dropdownVisible.handleComponentVisible();
    exportDDEX.mutate({
      id: musicProductDigitalId,
      ddexVersionId: 0,
      fileName: `${productName}_DDEX382.xml`,
    });
  };

  const onUpdateFromTrack = async () => {
    if (!musicProductDigitalId) return;
    await updateFromTrack.mutateAsync(
      { digitalMusicProductId: musicProductDigitalId },
      {
        onError: error => {
          onErrorShowToast(error);
        },
        onSuccess: async () => {
          await refetch();
        },
      }
    );
  };

  return (
    <Form.MusicProductDigital
      i18nextPrefix='music-product-digital'
      onSubmit={() => {}}
      initialValues={musicProductDigitalIndividualInitialValues}
      tittle={getMainTitle(musicProductDigital?.mainTitle)}
      disableTabs={
        musicProductDigital?.releaseStatus === STATUS.draft
          ? [
              musicProductDigitalTabs.delivery,
              musicProductDigitalTabs.smartlink,
              musicProductDigitalTabs.assetIds,
              musicProductDigitalTabs.statistics,
            ]
          : []
      }
      backPath='music-product-digital'
      status={{ displayMode: true, isLoading }}
      topbarContent={
        <div className='flex gap-2 items-center'>
          {musicProductDigital?.singleMismatch &&
            musicProductDigital?.productType === DIGITAL_PRODUCT_TYPE.audio && (
              <Button
                isClear
                disabled={!musicProductDigitalId || updateFromTrack.isPending}
                onClick={onUpdateFromTrack}>
                <Button.Icon icon={<PiStorefront />} />
                <Button.Text text={t('track.update-single')} />
              </Button>
            )}
          {hasPermission('DigitalMusicProductPermissionsLIST**', 2) && (
            <Button isClear onlyIcon onClick={goToEditMusicProductDigital}>
              <Button.Description description={t('button.edit')} />
              <Button.Icon icon={<PiPencilSimpleLine />} />
            </Button>
          )}
          {hasPermission('DigitalMusicProductPermissionsLIST**', 4) && (
            <MagellanPopups.DeleteWarning
              name={musicProductDigital?.mainTitle.title}
              onRemove={onRemoveMusicProductDigital}
            />
          )}
          <Button
            variant={BUTTON_VARIANT.default}
            onlyIcon
            isClear
            onClick={dropdownVisible.handleComponentVisible}
            outsideChildren={
              <DropdownMenu
                ref={dropdownVisible.ref}
                visible={dropdownVisible.isComponentVisible}
                style={{ padding: 5 }}
                justifyPosition='end'>
                <div className='flex flex-col w-full'>
                  {hasPermission('PhysicalMusicProductPermissionsLIST**', 1) &&
                    musicProductDigital?.productType ===
                      DIGITAL_PRODUCT_TYPE.audio && (
                      <Button
                        isClear
                        fullWidth
                        onClick={() => {
                          dropdownVisible.handleComponentVisible();
                          onCreateMusicProductPhysical();
                        }}
                        isLoading={createPhysical.isPending}>
                        <Button.Text
                          text={t('music-product-digital.create-physical')}
                        />
                      </Button>
                    )}
                  <Button
                    isClear
                    fullWidth
                    onClick={handleExportTXT}
                    disabled={exportTXT.isPending}>
                    <Button.Text
                      text={t('music-product-digital.export.to-txt')}
                    />
                  </Button>
                  {musicProductDigital?.releaseStatus === STATUS.verified && (
                    <Button
                      isClear
                      fullWidth
                      onClick={handleExportDDEX43}
                      disabled={exportDDEX.isPending}>
                      <Button.Text
                        text={t('music-product-digital.export.to-ddex43')}
                      />
                    </Button>
                  )}
                </div>
              </DropdownMenu>
            }>
            <Button.Description description={t('button.more-options')} />
            <Button.Icon icon={<PiDotsThreeVerticalBold />} />
          </Button>
        </div>
      }
    />
  );
};

export default MusicProductDigital;
